import { Stack } from 'component-library';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { BenchmarkType, PlotReportFactLabelNameEnum, R1FactType } from '@/api/rest/resources/types/fact';
import { GraphTileType } from '@/api/rest/resources/types/widget';
import { BiodiversityZoneChart } from '@/components/Charts';
import { ChartHeading } from '@/components/Charts/components/ChartHeading';
import { useChartZoom } from '@/components/Charts/components/ChartZoom';
import { ItemizedGraphTypeRenderer } from '@/components/Charts/components/ItemizedGraphType';
import { NUM_ITEMS_TYPE } from '@/components/Charts/hooks/usePlotsForItemizedGraph';
import { GraphFact } from '@/components/Charts/types';
import { GraphTileProps } from '@/components/IndicatorCard/GraphTile';
import { useSetIndicatorHasData } from '@/components/IndicatorCard/IndicatorContextProvider';
import { useGetCustomBenchmarks } from '@/hooks/useCustomBenchmarks';
import { useBenchmarksForOrganization } from '@/pages/shared/hooks/useBenchmarkForFact';
import { useOrganizationIndicatorGraphFact } from '@/pages/shared/hooks/useOrganizationFact';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';

import { NoData } from '../../../../components/NoData';

export const BiodiversityCardGraphTile = (props?: (GraphTileProps & { selectedBenchmark?: string | null }) | null) => {
  const { t } = useTranslation();

  const organizationId = useOrganizationId();

  const indicator = GraphTileType.protected_on_farm_habitat;
  const { data: customBenchmarksResp } = useGetCustomBenchmarks(organizationId, indicator);

  const selectedBenchmark = props?.selectedBenchmark;
  const activeCustomBenchmark = customBenchmarksResp?.results?.find((b) => b.id === selectedBenchmark);

  const { setIndicatorGraphData } = useSetIndicatorHasData();

  const { zoom, setZoom } = useChartZoom();
  const selectedZoom = props?.zoom ?? zoom; // props.zoom is used when this component is rendered as widget (e.g. measurement pages)
  const setZoomFunc = props?.setZoom ?? setZoom;
  const plotGroupId = props?.plotGroupId ?? null;

  const biodiversityZonePercentHistoricGraphFact = R1FactType.r1_biodiversity_zone_percent_historic_graph;
  const { [biodiversityZonePercentHistoricGraphFact]: biodiversityZonePercentHistoricGraph } =
    useOrganizationIndicatorGraphFact<GraphFact[]>(
      biodiversityZonePercentHistoricGraphFact,
      null,
      undefined,
      plotGroupId,
    );
  const bpHistoricData = biodiversityZonePercentHistoricGraph?.value;

  // TODO: MVP-3284 centralize this benchmarking formatting to a hook?^
  const bpLowBenchmarkFact = R1FactType.biodiversity_buffer_zone_pct_benchmark_graph;
  const { [bpLowBenchmarkFact]: bpLowBenchmark } = useOrganizationIndicatorGraphFact<GraphFact[]>(
    bpLowBenchmarkFact,
    null,
    {
      [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.low,
    },
    plotGroupId,
  );
  const bpLowBenchmarkData = (bpLowBenchmark?.value ?? []).map((fact) => {
    return { ...fact, name: `${fact.name}_low` };
  });

  const bpModerateBenchmarkFact = R1FactType.biodiversity_buffer_zone_pct_benchmark_graph;
  const { [bpModerateBenchmarkFact]: bpModerateBenchmark } = useOrganizationIndicatorGraphFact<GraphFact[]>(
    bpModerateBenchmarkFact,
    null,
    {
      [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.moderate,
    },
    plotGroupId,
  );
  const bpModerateBenchmarkData = (bpModerateBenchmark?.value ?? []).map((fact) => {
    return { ...fact, name: `${fact.name}_moderate` };
  });

  const bpHighBenchmarkFact = R1FactType.biodiversity_buffer_zone_pct_benchmark_graph;
  const { [bpHighBenchmarkFact]: bpHighBenchmark } = useOrganizationIndicatorGraphFact<GraphFact[]>(
    bpHighBenchmarkFact,
    null,
    {
      [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.high,
    },
    plotGroupId,
  );
  const bpHighBenchmarkData = (bpHighBenchmark?.value ?? []).map((fact) => {
    return { ...fact, name: `${fact.name}_high` };
  });

  const bpBenchmarks = useBenchmarksForOrganization(
    R1FactType.biodiversity_buffer_zone_pct_benchmark_graph,
    null,
    undefined,
    plotGroupId,
  );

  /**
   * We only null check for bpHistoricData since bpHistoricData is the primary source data of the graph
   * reference lines are displayed only when bpHistoricData is rendered
   */
  const noChartData = !bpHistoricData?.length;

  useEffect(() => {
    setIndicatorGraphData({ graphData: !noChartData });
  }, [noChartData, setIndicatorGraphData]);

  if (noChartData) {
    return <NoData />;
  }

  const chartData = [...bpHistoricData, ...bpLowBenchmarkData, ...bpModerateBenchmarkData, ...bpHighBenchmarkData];

  return (
    <Stack center data-testid='biodiversity-graph-tile'>
      <Stack spacing={2.5} className='w-full items-center justify-center px-1 py-6 lg:items-start lg:p-6 lg:pl-1'>
        {!props?.headerProps?.hideChartHeader && (
          <ChartHeading
            heading={t('shared.ncaDetail.details.biodiversity.labels.historicalGraphHeadline')}
            popover={{
              title: t('shared.ncaDetail.details.biodiversity.historicalData.title'),
              body: t('shared.ncaDetail.details.biodiversity.historicalData.body'),
            }}
            className='w-full px-6 sm:px-0'
            zoom={selectedZoom}
            setZoom={setZoom}
          />
        )}
        <ItemizedGraphTypeRenderer
          graphType={props?.graphType}
          indicator={GraphTileType.protected_on_farm_habitat}
          plotRenderType={props?.plotRenderType ?? NUM_ITEMS_TYPE.TOP_PLOTS}
          zoom={selectedZoom}
          setZoom={setZoomFunc}
          customBenchmark={activeCustomBenchmark}
          defaultGraphType={
            <BiodiversityZoneChart
              data={chartData}
              benchmarks={bpBenchmarks}
              customBenchmark={activeCustomBenchmark}
              showTooltip={true}
              className='relative w-full'
              data-testid='biodiversity-zone-chart'
              zoom={selectedZoom}
              setZoom={setZoomFunc}
            />
          }
        />
      </Stack>
    </Stack>
  );
};
